import React, { useRef, useState } from "react";
import { ScrollToTop } from "../../components";
import emailjs from "@emailjs/browser";
import "./contact.css";
import CTAButton from "../../ui/CTAButton";
import { Link } from "react-router-dom";
import { BsEnvelope, BsPhone, BsTelephoneFill } from "react-icons/bs";
import toast from "react-hot-toast";
import { toastOptions } from "../../data/toast-options";
import { FaSpinner } from "react-icons/fa";
import { useContactPage } from "../../hooks/useContactPage";

const ContactPage = () => {
  const form = useRef();
  const { contactData, isLoading: isContactLoading, error } = useContactPage();

  const initialState = {
    name: "",
    email: "",
    message: "",
  };

  const [{ name, email, message }, setState] = useState(initialState);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setState((prevState) => ({ ...prevState, [name]: value }));
  };

  const clearState = () => setState({ ...initialState });

  const sendEmail = async (e) => {
    e.preventDefault();

    setIsLoading(true);

    const AUTH_KEY = process.env.REACT_APP_MAIL_KEY;

    console.log("contact details", name, email, message);

    try {
      // Fetch the email template from the public folder
      const templateResponse = await fetch("/email-templates/contact.html");
      if (!templateResponse.ok) {
        throw new Error("Failed to load email template.");
      }
      let template = await templateResponse.text();

      // Replace placeholders with actual form data
      template = template
        .replace(/{{name}}/g, name)
        .replace(/{{email}}/g, email)
        .replace(/{{message}}/g, message);

      // Send the email via your API endpoint
      const response = await fetch(
        `${process.env.REACT_APP_MAIL_SERVER}/send`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: AUTH_KEY,
          },
          body: JSON.stringify({
            fromHost: process.env.REACT_APP_MAIL_USER,
            replyTo: email,
            to: process.env.REACT_APP_EMAIL_TO,
            subject: `Follow-Up: Additional Information from Contact Form Submission`,
            text: `Name: ${name}\nEmail: ${email}\nMessage: ${message}`,
            html: template,
          }),
        }
      );

      if (response.ok) {
        toast.success(
          "Message sent! We'll get back to you shortly.",
          toastOptions
        );
        clearState();
      } else {
        const errorText = await response.text();
        toast.error(
          `Couldn't send. Server responded with: ${response.status} - ${errorText}`,
          toastOptions
        );
      }
    } catch (err) {
      console.error("Error sending mail:", err);
      toast.error(
        `${err.message} - Couldn't send. Check your network connection.`,
        toastOptions
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (isContactLoading) {
    return (
      <div className="loading-container">
        <FaSpinner
          className="spinner"
          style={{ animation: "spin 1s linear infinite" }}
        />
        <p>Loading contact information...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p>Error loading contact information. Please try again later.</p>
      </div>
    );
  }

  return (
    <>
      <section className="contactPageSection">
        <div className="container">
          <div className="contactPageHeader">
            <h1>{contactData?.title || "Contact Us"}</h1>
          </div>
          <div className="map">
            <iframe
              src={contactData?.mapEmbed}
              width="100%"
              height="250"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Address"
            ></iframe>
          </div>
          <div className="addressForm">
            <div className="address">
              <small>
                {contactData?.address?.street},
                <br />
                {contactData?.address?.city}, {contactData?.address?.province}{" "}
                {contactData?.address?.postalCode},
                <br />
                {contactData?.address?.country}
              </small>
              <br /> <br />
              <small>
                <div className="infoContainer">
                  <BsTelephoneFill className="icon gold" />
                  {contactData?.contactInfo?.phone}
                </div>
                <br />
                <div className="infoContainer">
                  <BsEnvelope className="icon gold" />
                  <Link
                    className="blue"
                    to={`mailto:${contactData?.contactInfo?.email}`}
                  >
                    {contactData?.contactInfo?.email}
                  </Link>
                </div>
              </small>
            </div>
            <div className="contactForm">
              <form ref={form} onSubmit={sendEmail}>
                <input
                  type="text"
                  name="name"
                  placeholder="Your Full Name"
                  onChange={handleChange}
                  value={name}
                  required
                  disabled={isLoading}
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Your Email"
                  onChange={handleChange}
                  value={email}
                  required
                  disabled={isLoading}
                />
                <textarea
                  name="message"
                  rows="7"
                  placeholder="Your Message"
                  onChange={handleChange}
                  value={message}
                  required
                  disabled={isLoading}
                ></textarea>
                <CTAButton
                  type="submit"
                  specialClass="blueBkg"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <FaSpinner
                        className="spinner"
                        style={{
                          marginRight: "8px",
                          animation: "spin 1s linear infinite",
                        }}
                      />
                      Sending...
                    </>
                  ) : (
                    "Send"
                  )}
                </CTAButton>
              </form>
            </div>
          </div>
        </div>
        <div className="redWave" />
      </section>
      <ScrollToTop />
    </>
  );
};

export default ContactPage;
